import { DigitalTwinSettingCreationRequest, TimeSeriesSettingValue } from 'api/digitalTwin/digitalTwin.api'
import { Dispatch, SetStateAction } from 'react'

// Constants for scrolling when dragging to copy table values.
const SCROLL_INTERVAL_MILLISECONDS = 30
const SCROLL_AMOUNT_EACH_STEP = 4
const DISTANCE_FROM_EDGE_TO_START_SCROLLING = 60

export const updateTimeSeriesValues = (
  newSettingValues: TimeSeriesSettingValue,
  index: number,
  settingsData: DigitalTwinSettingCreationRequest[],
  getSettingData: (
    settingData: DigitalTwinSettingCreationRequest[],
    index: number
  ) => DigitalTwinSettingCreationRequest | undefined,
  times: ISODateTime[],
  getTableKey: (values: any[]) => string,
  setTableKey: (key: string) => void,
  setSettingData: (data: any) => void
): void => {
  const settingData = getSettingData(settingsData, index)
  if (settingData === undefined) {
    return
  }

  const newSettingValue = {
    ...(settingData.value as TimeSeriesSettingValue),
    ...newSettingValues,
  }

  const newSettingData = {
    ...settingData,
    value: newSettingValue,
  }

  const newValuesArray = getValuesArrayFromSettingValue(times, newSettingValue)
  const newTableKey = getTableKey(newValuesArray)
  setTableKey(newTableKey)
  setSettingData(newSettingData)
}

export const getCopiedDatasetSettingValues = (
  settingValue: TimeSeriesSettingValue,
  dataset: Dataset,
  selectedRowIndices?: number[]
): TimeSeriesSettingValue => {
  const newSettingValues = { ...settingValue }

  dataset.times.forEach((datasetTime, index) => {
    if (selectedRowIndices !== undefined && selectedRowIndices.length > 0 && !selectedRowIndices.includes(index)) {
      return
    }

    const time = datasetTime as ISODateTime
    const datasetValue = dataset.values[index]

    newSettingValues[time] = {
      ...settingValue[time],
      value: datasetValue !== null ? datasetValue : settingValue[time].value,
    }
  })

  return newSettingValues
}

export const shouldClearTimeSeriesSelectionOnDocumentMouseDown = (
  target: EventTarget | null,
  tableContainerElement: HTMLDivElement | null,
  calculateFormElement: HTMLElement | null = null
): boolean => {
  if (!(target instanceof HTMLElement)) {
    return false
  }

  if (tableContainerElement?.contains(target)) {
    return false
  }

  // The calculate-settings form operates on the current row selection, so interacting with it
  // (e.g. pressing the Calculate button) must not clear that selection.
  if (calculateFormElement?.contains(target)) {
    return false
  }

  // Menus and listboxes are portalled to the document body, so they land outside both elements above
  // even when they were opened from inside one of them.
  if (target.closest('[role="menu"], [role="menuitem"], [role="listbox"], [role="option"]')) {
    return false
  }

  return true
}

export const handleMouseMove = (
  event: React.MouseEvent<HTMLTableCellElement>,
  mouseDownValue: { rowIndex: number; value: number | null } | null,
  tableContainerRef: React.RefObject<HTMLDivElement | null>,
  dialogContentRef: React.RefObject<HTMLDivElement | null> | undefined,
  scrollDirectionRef: React.MutableRefObject<'none' | 'up' | 'down'>,
  intervalId: NodeJS.Timeout | null,
  setIntervalId: Dispatch<SetStateAction<NodeJS.Timeout | null>>
): void => {
  if (mouseDownValue !== null) {
    // If mouse was moved to top or bottom of the table while some of the table is off screen, start scrolling
    checkScrollDirection(
      event.nativeEvent,
      tableContainerRef,
      dialogContentRef,
      scrollDirectionRef,
      intervalId,
      setIntervalId
    )
  }
}

export const setScrollDirection = (
  scrollDirection: 'none' | 'up' | 'down',
  scrollDirectionRef: React.MutableRefObject<'none' | 'up' | 'down'>,
  intervalId: NodeJS.Timeout | null,
  setIntervalId: Dispatch<SetStateAction<NodeJS.Timeout | null>>,
  dialogContentRef: React.RefObject<HTMLDivElement | null> | undefined,
  tableContainerRef: React.RefObject<HTMLDivElement | null>
): void => {
  scrollDirectionRef.current = scrollDirection

  if (scrollDirection !== 'none' && intervalId === null && dialogContentRef) {
    let scrollAmount = 0 // Start at 0, then increase/decrease each interval step

    // Scroll continuously while the mouse is down
    const newIntervalId = window.setInterval(() => {
      const dialogContent = dialogContentRef?.current
      const tableContainerRect = tableContainerRef.current?.getBoundingClientRect()
      const dialogContentRect = dialogContent?.getBoundingClientRect()

      if (!dialogContent || !tableContainerRect || !dialogContentRect) {
        return
      }

      // Don't continue scrolling past the table in either direction
      const scrolledToBottom =
        scrollDirectionRef.current === 'down' && tableContainerRect.bottom < dialogContentRect.bottom
      const scrolledToTop = scrollDirectionRef.current === 'up' && tableContainerRect.top > dialogContentRect.top

      if (scrolledToBottom || scrolledToTop) {
        window.clearInterval(newIntervalId)
        setIntervalId(null)
        return
      }

      // Scroll down a little each interval step
      dialogContent.scrollTo(0, dialogContent.scrollTop + scrollAmount)

      if (scrollDirectionRef.current === 'down') {
        scrollAmount += SCROLL_AMOUNT_EACH_STEP
      } else if (scrollDirectionRef.current === 'up') {
        scrollAmount -= SCROLL_AMOUNT_EACH_STEP
      }
    }, SCROLL_INTERVAL_MILLISECONDS)

    setIntervalId(newIntervalId as unknown as NodeJS.Timeout)
  } else if (intervalId !== null) {
    // Stop scrolling when the scrollDirection is 'none' or dialogContent is no longer set
    window.clearInterval(intervalId)
    setIntervalId(null)
  }
}

export const checkScrollDirection = (
  event: MouseEvent,
  tableContainerRef: React.RefObject<HTMLDivElement | null>,
  dialogContentRef: React.RefObject<HTMLDivElement | null> | undefined,
  scrollDirectionRef: React.MutableRefObject<'none' | 'up' | 'down'>,
  intervalId: NodeJS.Timeout | null,
  setIntervalId: Dispatch<SetStateAction<NodeJS.Timeout | null>>
): void => {
  const tableContainerRect = tableContainerRef.current?.getBoundingClientRect()
  const dialogContentRect = dialogContentRef?.current?.getBoundingClientRect()

  if (!tableContainerRect || !dialogContentRect) {
    return
  }

  const { bottom: tableBottom, top: tableTop } = tableContainerRect
  const { bottom: modalBottom, top: modalTop } = dialogContentRect

  const mouseCloseToTop = event.clientY < modalTop + DISTANCE_FROM_EDGE_TO_START_SCROLLING
  const mouseCloseToBottom = event.clientY > modalBottom - DISTANCE_FROM_EDGE_TO_START_SCROLLING

  const tableOutsideModalTop = tableTop < modalTop
  const tableOutsideModalBottom = tableBottom > modalBottom

  const shouldScrollUp = tableOutsideModalTop && mouseCloseToTop
  const shouldScrollDown = tableOutsideModalBottom && mouseCloseToBottom

  const currentScrollDirection = scrollDirectionRef.current

  if (shouldScrollUp && currentScrollDirection !== 'up') {
    setScrollDirection('up', scrollDirectionRef, intervalId, setIntervalId, dialogContentRef, tableContainerRef)
  } else if (shouldScrollDown && currentScrollDirection !== 'down') {
    setScrollDirection('down', scrollDirectionRef, intervalId, setIntervalId, dialogContentRef, tableContainerRef)
  } else if (!shouldScrollUp && !shouldScrollDown && currentScrollDirection !== 'none') {
    setScrollDirection('none', scrollDirectionRef, intervalId, setIntervalId, dialogContentRef, tableContainerRef)
  }
}

function getValuesArrayFromSettingValue(
  times: ISODateTime[],
  value?: number | null | { [time: ISODateTime]: { id?: number; value: number | null } }
): { id?: number; value: number | null }[] {
  return times.map((time) => (value && typeof value === 'object' && time in value ? value[time] : { value: 0 }))
}
